import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  Unsubscribe,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../lib/firebase/config';

/**
 * Glance — a Master Admin can request to watch another user's screen in
 * real-time. Implemented as one-way WebRTC (target → viewer) with offer /
 * answer / ICE candidates exchanged through Firestore. Reuses Google's
 * public STUN servers; works on most office networks without TURN.
 *
 * Document shape:
 *   glanceSessions/{sessionId}
 *     viewerUid, viewerName, targetUid, targetName,
 *     status: 'requesting' | 'accepted' | 'streaming' | 'declined' | 'ended',
 *     offer?: { type, sdp }, answer?: { type, sdp },
 *     reason?: string, createdAt, updatedAt
 *
 *     /viewerCandidates/{candidateId}  ICE from viewer
 *     /targetCandidates/{candidateId}  ICE from target
 *
 * The viewer cleans the doc up on close; if the viewer crashes, the listener
 * tears down anything older than 5 minutes when starting a new request.
 */

export const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export type GlanceStatus =
  | 'requesting'
  | 'accepted'
  | 'streaming'
  | 'declined'
  | 'ended';

export interface GlanceSession {
  id: string;
  viewerUid: string;
  viewerName: string;
  targetUid: string;
  targetName: string;
  status: GlanceStatus;
  reason?: string;
  offer?: RTCSessionDescriptionInit;
  answer?: RTCSessionDescriptionInit;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

const COLLECTION = 'glanceSessions';

// -------------------- session CRUD --------------------

export async function createGlanceRequest(args: {
  viewerUid: string;
  viewerName: string;
  targetUid: string;
  targetName: string;
}): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    viewerUid: args.viewerUid,
    viewerName: args.viewerName,
    targetUid: args.targetUid,
    targetName: args.targetName,
    status: 'requesting' as GlanceStatus,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function setGlanceStatus(
  sessionId: string,
  status: GlanceStatus,
  reason?: string,
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, sessionId), {
    status,
    reason: reason || null,
    updatedAt: serverTimestamp(),
  });
}

export async function setGlanceOffer(
  sessionId: string,
  offer: RTCSessionDescriptionInit,
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, sessionId), {
    offer: { type: offer.type, sdp: offer.sdp },
    updatedAt: serverTimestamp(),
  });
}

export async function setGlanceAnswer(
  sessionId: string,
  answer: RTCSessionDescriptionInit,
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, sessionId), {
    answer: { type: answer.type, sdp: answer.sdp },
    updatedAt: serverTimestamp(),
  });
}

export function subscribeGlanceSession(
  sessionId: string,
  onChange: (s: GlanceSession | null) => void,
): Unsubscribe {
  const ref = doc(db, COLLECTION, sessionId);
  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) {
      onChange(null);
      return;
    }
    const data = snap.data();
    onChange({
      id: snap.id,
      viewerUid: data.viewerUid,
      viewerName: data.viewerName,
      targetUid: data.targetUid,
      targetName: data.targetName,
      status: data.status,
      reason: data.reason ?? undefined,
      offer: data.offer ?? undefined,
      answer: data.answer ?? undefined,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    } as GlanceSession);
  });
}

/**
 * Subscribe to inbound glance requests for the given user (target side).
 * Yields any session in which they are the target. Filters happen in the
 * listener since collection-wide queries with `where` need an index.
 */
export function subscribeIncomingGlance(
  targetUid: string,
  onMatch: (session: GlanceSession) => void,
): Unsubscribe {
  return onSnapshot(collection(db, COLLECTION), (snap) => {
    snap.docChanges().forEach((change) => {
      if (change.type === 'removed') return;
      const data = change.doc.data();
      if (data.targetUid !== targetUid) return;
      onMatch({
        id: change.doc.id,
        viewerUid: data.viewerUid,
        viewerName: data.viewerName,
        targetUid: data.targetUid,
        targetName: data.targetName,
        status: data.status,
        reason: data.reason ?? undefined,
        offer: data.offer ?? undefined,
        answer: data.answer ?? undefined,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      } as GlanceSession);
    });
  });
}

// -------------------- ICE --------------------

export async function pushIceCandidate(
  sessionId: string,
  side: 'viewer' | 'target',
  candidate: RTCIceCandidate,
): Promise<void> {
  await addDoc(
    collection(db, COLLECTION, sessionId, side === 'viewer' ? 'viewerCandidates' : 'targetCandidates'),
    candidate.toJSON(),
  );
}

export function subscribeIceCandidates(
  sessionId: string,
  side: 'viewer' | 'target',
  onCandidate: (c: RTCIceCandidateInit) => void,
): Unsubscribe {
  return onSnapshot(
    collection(db, COLLECTION, sessionId, side === 'viewer' ? 'viewerCandidates' : 'targetCandidates'),
    (snap) => {
      snap.docChanges().forEach((change) => {
        if (change.type !== 'added') return;
        onCandidate(change.doc.data() as RTCIceCandidateInit);
      });
    },
  );
}

// -------------------- cleanup --------------------

/**
 * Best-effort destroy of a session — removes any ICE subdocs and the
 * session document itself. Safe to call even if the session is partially
 * cleaned up already.
 */
export async function destroyGlanceSession(sessionId: string): Promise<void> {
  try {
    const ref = doc(db, COLLECTION, sessionId);
    const [vSnap, tSnap] = await Promise.all([
      getDocs(collection(db, COLLECTION, sessionId, 'viewerCandidates')),
      getDocs(collection(db, COLLECTION, sessionId, 'targetCandidates')),
    ]);
    await Promise.all([
      ...vSnap.docs.map((d) => deleteDoc(d.ref)),
      ...tSnap.docs.map((d) => deleteDoc(d.ref)),
    ]);
    // Mark ended first so subscribers can react before delete races.
    await setDoc(ref, { status: 'ended', updatedAt: serverTimestamp() }, { merge: true });
    await deleteDoc(ref);
  } catch (err) {
    // Non-fatal — Firestore TTL/cleanup can sweep stragglers.
    console.warn('Failed to destroy glance session', sessionId, err);
  }
}
